import { useMemo } from 'react';
import { resolveCommonAction } from './labels';
import { useModuleTranslation } from './useModuleTranslation';
import { translateToolbarTitle } from './moduleT';
import { useTranslation } from 'react-i18next';

type ModuleStringsConfig = {
  module?: { title?: string };
  search?: { placeholder?: string };
  emptyStates?: {
    noData?: string;
    noSearchResults?: string;
    noFilterResults?: string;
  };
  delete?: {
    title?: string;
    message?: string;
    confirmText?: string;
    cancelText?: string;
    errorMessage?: string;
  };
  toolbar?: {
    actions?: Array<{ key: string; title: string }>;
  };
};

/** Translate common moduleConfig strings for bespoke list/detail pages. */
export function useModuleConfigStrings(i18nKey: string | undefined, config: ModuleStringsConfig) {
  const { t } = useTranslation('common');
  const { mt, enabled } = useModuleTranslation(i18nKey);

  return useMemo(() => {
    const tr = (path: string, fallback: string | undefined) =>
      enabled && fallback ? mt(path, fallback) : fallback ?? '';

    const deleteCfg = config.delete;
    const toolbarAdd = config.toolbar?.actions?.find((a) => a.key === 'add');

    return {
      title: tr('title', config.module?.title),
      searchPlaceholder: tr('searchPlaceholder', config.search?.placeholder),
      emptyNoData: tr('emptyStates.noData', config.emptyStates?.noData),
      emptyNoSearchResults: tr('emptyStates.noSearchResults', config.emptyStates?.noSearchResults),
      emptyNoFilterResults: tr('emptyStates.noFilterResults', config.emptyStates?.noFilterResults),
      deleteTitle: tr('delete.title', deleteCfg?.title),
      deleteMessage: tr('delete.message', deleteCfg?.message),
      deleteConfirmText: deleteCfg?.confirmText
        ? resolveCommonAction(t, tr('delete.confirmText', deleteCfg.confirmText))
        : undefined,
      deleteCancelText: deleteCfg?.cancelText
        ? resolveCommonAction(t, tr('delete.cancelText', deleteCfg.cancelText))
        : undefined,
      deleteErrorMessage: tr('delete.errorMessage', deleteCfg?.errorMessage),
      addActionTitle: toolbarAdd
        ? enabled
          ? translateToolbarTitle(mt, toolbarAdd.key, toolbarAdd.title)
          : toolbarAdd.title
        : undefined,
    };
  }, [enabled, mt, t, config]);
}
